import React, {useContext, useEffect} from 'react';
import {FormattedMessage} from "react-intl";
import {Redirect} from "react-router-dom";

import store from "store";

import Grid from "@mui/material/Grid";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";

import StyledLine from "components/List/components/StyledLine";
import IconButton from "components/button/IconButton";
import EditIcon from "components/icons/EditIcon";
import {ListContext} from "components/List/context/ListContext";

import ShippingIcon from "views/picking/components/shippingIcon";

import Header from "./components/Header";
import ActiveButton from "./components/ActiveButton";
import ShippingMethodName from "./components/ShippingMethodName";
import SyncStatusBadge from "./components/SyncStatusBadge";
import PushNowButton from "./components/PushNowButton";
import SyliusCandidateLine from "./components/SyliusCandidateLine";
import SyliusFetchReport from "./components/SyliusFetchReport";
import ShippingMethodForm from "views/shippingMethods/components/ShippingMethodForm";

import {list} from "api/shippingMethod/list";
import {listSyliusCandidates} from "api/shippingMethod/syliusCandidates";
import {SyliusCandidatesContext} from "views/shippingMethods/context/SyliusCandidatesContext";

import {Authorizations, hasAuthorization} from "utils/authorizations";

function ShippingMethodList(props) {
    const {
        setLoading,
        isCreateDialogOpen,
        closeCreateDialog,
        openCreateDialog,
        setListItems,
        setNotFilteredListItems,
        listItems
    } = useContext(ListContext)

    const {candidates, setCandidates, setPrefill} = useContext(SyliusCandidatesContext);

    const authorizations = store.getState().authentication?.authorizations;

    const isAuthorized = hasAuthorization(authorizations, Authorizations.MARKETPLACE_SHIPPING_METHODS_MANAGEMENT);

    // componentDidMount
    useEffect(() => {
        if (isAuthorized) {
            getShippingMethods();
            refreshCandidates();
        }
    }, []);

    // a configured candidate drops out of the list, the API leaving out the codes known here
    function refreshCandidates() {
        listSyliusCandidates().then(setCandidates).catch(() => setCandidates([]));
    }

    function closeDialog() {
        closeCreateDialog();
        setPrefill(null);
    }

    function getShippingMethods() {
        setLoading(true)

        list('order[updatedAt]=desc').then((response) => {
            if (!response) {
                setLoading(false);

                return;
            }

            setListItems(response);
            setNotFilteredListItems(response);

            setLoading(false);
        })
    }

    // a Redirect returned from an effect is never rendered, it has to be returned here
    if (false === isAuthorized) {
        return <Redirect to={'/'} />;
    }

    return (
        <Grid container>
            <SyliusFetchReport />
            <Header />
            <Grid item container>
                {listItems.map((shippingMethod, index) => (
                    <Grid
                        item
                        container
                        key={shippingMethod.id}
                        sx={{padding: '8px 0'}}
                    >
                        <StyledLine
                            justifyContent={"space-between"}
                            alignItems={'center'}
                            {...props}
                        >
                            <Grid
                                item
                                container
                                xs={4}
                                columnSpacing={2}
                                alignItems={'center'}
                            >
                                <Grid item>
                                    <ShippingIcon
                                        shippingMethod={shippingMethod}
                                        noCircle={true}
                                        size={'xs'}
                                    />
                                </Grid>
                                <Grid item>
                                    <ShippingMethodName name={shippingMethod.name} />
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                xs={4}
                                columnSpacing={2}
                                justifyContent={'space-between'}
                            >
                                <Grid item>
                                    <FormattedMessage id={`shippingMethods.discr.${shippingMethod.discr}`}/>
                                </Grid>
                                <Grid item>
                                    {shippingMethod.shopName}
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                xs={4}
                                columnSpacing={2}
                                justifyContent={'flex-end'}
                                flexWrap={'nowrap'}
                                alignItems={'center'}
                            >
                                <Grid item sx={{ width: '170px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <SyncStatusBadge shippingMethod={shippingMethod} />
                                    <PushNowButton shippingMethod={shippingMethod} onPushed={() => getShippingMethods()} />
                                </Grid>
                                <Grid item sx={{ width: '60px' }}>
                                    <ActiveButton
                                        shippingMethod={shippingMethod}
                                        onClick={() => getShippingMethods()}
                                    />
                                </Grid>
                                <Grid item sx={{ width: '80px' }}>
                                    <Grid item>
                                        <IconButton onMouseDown={(e) => {
                                            e.stopPropagation();
                                            openCreateDialog(shippingMethod)
                                        }}>
                                            <EditIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </StyledLine>
                    </Grid>
                ))}
                {candidates.map(candidate => (
                    <SyliusCandidateLine key={candidate.id} candidate={candidate} />
                ))}
            </Grid>
            <Dialog
                open={isCreateDialogOpen}
                onClose={() => closeDialog()}
                maxWidth={'sm'}
                scroll={'body'}
            >
                <DialogContent>
                    <ShippingMethodForm
                        refreshList={() => {
                            getShippingMethods();
                            refreshCandidates();
                        }}
                        onClose={() => setPrefill(null)}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    );
}

export default ShippingMethodList;
